import {SortingState} from '@tanstack/table-core';
import React, {useEffect, useMemo, useState} from 'react';
import {useSearchParams} from 'react-router-dom';
import {v4 as uuid} from 'uuid';
import {useStore} from 'zustand';
import {Box, Stack} from '@mui/material';
import {
  designSystemToken,
  Headline,
  SwitchButton,
} from '@lightricks/react-design-system';
import useUiInteractionEvent from '@contentlab/hooks/analytics/use-interaction-event';
import useViewDismissedEvent from '@contentlab/hooks/analytics/use-view-dismissed-event';
import useViewPresentedEvent from '@contentlab/hooks/analytics/use-view-presented-event';
import {
  CONTENT_STUDIO_VIEW_NAMES,
  FLOW_NAMES,
} from '@/lib/analytics/analyticsConstants';
import queryClient from '@/utils/reactQueryClient';
import {getResolutionFromDimensions} from '@/utils/resolution';
import translate from '@/utils/translate';
import AssetPreviewModal from '@/views/content-studio/components/asset-preview-modal/AssetPreviewModal';
import AssetPreviewModalContent from '@/views/content-studio/components/asset-preview-modal/asset-preview-modal-content/AssetPreviewModalContent';
import AssetPreviewModalTitle from '@/views/content-studio/components/asset-preview-modal/asset-preview-modal-title/AssetPreviewModalTitle';
import {PreviewAsset} from '@/views/content-studio/components/asset-preview-modal/types';
import openDoAndDontModalFeatureIfNeeded from '@/views/content-studio/utils/openDoAndDontModalFeatureIfNeeded';
import FloatingPopperBar from '@/components/floating-popper-bar/FloatingPopperBar';
import {ASSET_FILTERS, CONTENT_LAB} from '@/hooks/content-studio/consts';
import useAssetBarActions from '@/hooks/content-studio/use-asset-bar-actions';
import useAssetsQuery from '@/hooks/content-studio/use-assets-query';
import {
  AssetsQueryFilters,
  AssetsQuerySorting,
} from '@/hooks/content-studio/use-assets-query/useAssetsQuery';
import useFiltersQuery from '@/hooks/content-studio/use-filters';
import useShouldPollForUploads from '@/hooks/content-studio/use-should-poll-for-uploads';
import contentStudioStore, {
  contentStudioActions,
  FilterOptions,
} from '@/stores/contentStudioStore';
import AssetActionsBar from '../components/asset-actions-bar/AssetActionsBar';
import FolderPopper from '../components/asset-actions-bar/FolderPopper';
import RenamePopper from '../components/asset-actions-bar/RenamePopper';
import SingleActionBar from '../components/asset-actions-bar/SingleActionBar';
import AssetTable from '../components/asset-table/AssetTable';
import {AssetCategory} from '../components/asset-table/types';
import FileUploaderButton from '../components/file-upload/FileUploaderButton';
import FilterButton from '../components/filter-button/FilterButton';
import MenuChip from '../components/menu-chip/MenuChip';
import styles from './Assets.module.scss';
import FeatureAssetPageTitle from './components/FeatureAssetPageTitle';
import EmptyAssetsState from './components/empty-assets-state/EmptyAssetsState';
import FeatureDetails from './type';
import {checkIncomingAssets, displayFilesAsUploading} from './utils';

export default function Assets({
  featureDetails,
}: {
  featureDetails?: FeatureDetails;
}) {
  useEffect(() => {
    openDoAndDontModalFeatureIfNeeded(featureDetails?.id);
  }, []);
  const [searchParams, setSearchParams] = useSearchParams();

  const contentType = useMemo(() => {
    return (
      (searchParams.get('contentType') as AssetCategory) ||
      AssetCategory.UPLOADS
    );
  }, [searchParams]);

  const {
    selectedAssets,
    uploadsSorting,
    resultsSorting,
    uploadsFilters,
    resultsFilters,
    files,
  } = useStore(contentStudioStore);
  const {uiInteraction} = useUiInteractionEvent();
  const {viewPresented} = useViewPresentedEvent();
  const {viewDismissed} = useViewDismissedEvent();
  const [openPreviewModal, setOpenPreviewModal] = useState(false);
  const [previewModalPresentationId, setPreviewModalPresentationId] =
    useState<string>(uuid());
  const [previewModalAssetIndex, setPreviewModalAssetIndex] = useState(0);
  const [selectedOnlyMode, setSelectedOnlyMode] = useState(false);

  useEffect(() => {
    searchParams.forEach((value, key) => {
      if (key in FilterOptions) {
        contentStudioActions.handleFilter(
          key as FilterOptions,
          value,
          contentType === AssetCategory.RESULTS ? 'results' : 'uploads'
        );
      }
    });
    queryClient.invalidateQueries({
      queryKey: [CONTENT_LAB, ASSET_FILTERS],
    });
  }, []);

  const {data: filtersData} = useFiltersQuery();

  // Calculate if polling should be enabled based on current store state
  const shouldPollForUploads = useShouldPollForUploads();

  const {
    data: uploadsData,
    fetchNextPage: fetchNextUploadsPage,
    isLoading: isUploadsLoading,
    isFetching: isUploadsFetching,
  } = useAssetsQuery(
    uploadsSorting,
    {
      ...uploadsFilters,
      type: 'uploads',
    } as AssetsQueryFilters,
    shouldPollForUploads
  );

  const {
    data: resultsData,
    fetchNextPage: fetchNextResultsPage,
    isLoading: isResultsLoading,
    isFetching: isResultsFetching,
    refetch: refetchResults,
  } = useAssetsQuery(resultsSorting, {
    ...resultsFilters,
    type: 'results',
  } as AssetsQueryFilters);

  const {data: allUploadsData} = useAssetsQuery(uploadsSorting, {
    type: 'uploads',
  } as AssetsQueryFilters);

  const {data: allResultsData} = useAssetsQuery(resultsSorting, {
    type: 'results',
  } as AssetsQueryFilters);

  const handleSortingChange = (
    newSorting: SortingState,
    setter: (sorting: AssetsQuerySorting) => void
  ) => {
    const newState = newSorting[0];
    if (newState) {
      setter({
        field: newState.id,
        desc: newState.desc,
      });
    }
  };

  const checkedUploadsData = useMemo(() => {
    return checkIncomingAssets(uploadsData?.pages || [], selectedAssets, files);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uploadsData]);
  useEffect(() => {
    checkedUploadsData.assetsToUpdate.forEach((asset) => {
      contentStudioActions.updateSelectedAsset({
        id: asset.id,
        isUploading: false,
        thumbnail: asset.thumbnailUrl,
        type: 'Uploads',
        fileName: asset.name,
        url: asset.url,
        durationMillis: asset.metadata.durationMillis,
        aspectRatio: asset.metadata.aspectRatio,
      });
    });
    checkedUploadsData.filesToRemove.forEach((file) => {
      contentStudioActions.removeFile(file);
    });
  }, [checkedUploadsData]);

  const uploadingAssets = displayFilesAsUploading(files);

  const allResultsAssets = useMemo(
    () =>
      allResultsData?.pages
        .flatMap((page) => page)
        .map((asset) => ({...asset, type: 'result' as const}))
        .filter((asset) =>
          selectedAssets.some((selectedAsset) => selectedAsset.id === asset.id)
        ) || [],
    [allResultsData?.pages, selectedAssets]
  );

  const checkedAllUploadsData = useMemo(() => {
    return checkIncomingAssets(
      allUploadsData?.pages || [],
      selectedAssets,
      files
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allUploadsData]);

  const allUploadAssets = [
    ...checkedAllUploadsData.assetsToDisplay,
    ...uploadingAssets,
  ]
    .filter((asset) =>
      selectedAssets.some((selectedAsset) => selectedAsset.id === asset.id)
    )
    .map((asset) => ({...asset, type: 'upload' as const}));

  const selectedAssetsData = [...allResultsAssets, ...allUploadAssets];
  const {
    folderPopperElement,
    setFolderPopperElement,
    renamePopperElement,
    setRenamePopperElement,
    actions,
  } = useAssetBarActions(selectedAssetsData);

  const DisplayedAssets = () => {
    const contentTypeToAssets = {
      [AssetCategory.UPLOADS]: [
        ...uploadingAssets,
        ...checkedUploadsData.assetsToDisplay,
      ],
      [AssetCategory.RESULTS]: resultsData?.pages.flatMap((page) => page) || [],
    };

    if (selectedOnlyMode) {
      return selectedAssetsData;
    }

    return contentTypeToAssets[contentType];
  };

  const previewModalAsset = (): PreviewAsset | null => {
    if (DisplayedAssets().length === 0 || !openPreviewModal) {
      return null;
    }
    const asset = DisplayedAssets()[previewModalAssetIndex];

    if (!asset) {
      return null;
    }

    return {
      id: asset.id,
      name: asset.name,
      createdAt: asset.createdAt,
      metadata: {
        durationMillis: asset.metadata.durationMillis,
        fileSizeBytes: asset.metadata.fileSizeBytes,
        language: asset.metadata.language,
        aspectRatio: asset.metadata.aspectRatio,
        resolution: getResolutionFromDimensions(
          asset.metadata.height,
          asset.metadata.width
        ),
      },
      originalAssetName: asset.originalAsset?.name,
      originalAssetId: asset.originalAsset?.id,
      url: asset.url,
    };
  };

  const nextAssetClickHandler = () => {
    setPreviewModalAssetIndex(
      Math.min(previewModalAssetIndex + 1, DisplayedAssets().length - 1)
    );
  };

  const previousAssetClickHandler = () => {
    setPreviewModalAssetIndex(Math.max(previewModalAssetIndex - 1, 0));
  };

  const openPreviewHandler = (assetId: string) => {
    const currentPreviewModalPresentationId = uuid();
    const interactionId = uuid();
    setPreviewModalPresentationId(currentPreviewModalPresentationId);
    uiInteraction({
      item_name: 'asset_row',
      interaction_location: CONTENT_STUDIO_VIEW_NAMES.ASSETS,
      asset_id: assetId,
      interaction_id: interactionId,
      flow_name: FLOW_NAMES.CONTENT_STUDIO.ASSETS,
    });
    const index = DisplayedAssets().findIndex((asset) => {
      return asset.id === assetId && asset.url;
    });
    setPreviewModalAssetIndex(index);
    setOpenPreviewModal(true);
    viewPresented({
      view_name: CONTENT_STUDIO_VIEW_NAMES.ASSET_PREVIEW,
      view_type: 'modal',
      view_location: CONTENT_STUDIO_VIEW_NAMES.ASSETS,
      presentation_id: currentPreviewModalPresentationId,
      interaction_id: interactionId,
      source: 'item_pressed',
      view_details: `asset_id: ${assetId}`,
    });
  };

  const shouldOpenFloatingPopperBar = () => {
    const displayedAssets = DisplayedAssets();
    return (
      selectedAssets.length > 0 &&
      selectedAssets.some((asset) =>
        displayedAssets.find((a) => a.id === asset.id)
      )
    );
  };

  const emptyStateComponent = () => {
    return (
      <div className={styles.emptyStateContainer}>
        <EmptyAssetsState featureName={featureDetails?.name} />
      </div>
    );
  };

  useEffect(() => {
    if (selectedAssets.length === 0) {
      setSelectedOnlyMode(false);
    }
  }, [selectedAssets]);

  const anyAssetNotReady = selectedAssets.some((a) => a.isUploading);

  return (
    <Stack
      direction="column"
      width="85%"
      alignItems="center"
      margin="auto"
      height="100vh"
      overflow="hidden"
      style={featureDetails && {maxHeight: 'calc(100vh - 64px)'}}
    >
      <Stack
        direction="row"
        width="100%"
        justifyContent="space-between"
        marginY="16px"
      >
        {featureDetails ? (
          <FeatureAssetPageTitle featureDetails={featureDetails} />
        ) : (
          <Headline size="lg">{translate('Media Library')}</Headline>
        )}
        <Stack direction="row" alignItems="center" gap="8px">
          <MenuChip
            label={translate('Originals')}
            disabled={selectedOnlyMode}
            selectedOnlyMode={selectedOnlyMode}
            isActive={contentType === AssetCategory.UPLOADS}
            onClick={() => {
              uiInteraction({
                item_name: 'uploaded',
                interaction_location: CONTENT_STUDIO_VIEW_NAMES.ASSETS,
                flow_name: FLOW_NAMES.CONTENT_STUDIO.ASSETS,
              });
              setSearchParams(
                {
                  ...Object.fromEntries(searchParams.entries()),
                  contentType: AssetCategory.UPLOADS,
                },
                {replace: true}
              );
            }}
          />
          <MenuChip
            label={translate('Results')}
            selectedOnlyMode={selectedOnlyMode}
            isActive={contentType === AssetCategory.RESULTS}
            disabled={
              resultsData?.pages.flatMap((page) => page).length === 0 ||
              selectedOnlyMode
            }
            onClick={() => {
              uiInteraction({
                item_name: 'edited',
                interaction_location: CONTENT_STUDIO_VIEW_NAMES.ASSETS,
                flow_name: FLOW_NAMES.CONTENT_STUDIO.ASSETS,
              });
              setSearchParams(
                {
                  ...Object.fromEntries(searchParams.entries()),
                  contentType: AssetCategory.RESULTS,
                },
                {replace: true}
              );
              refetchResults();
            }}
          />
        </Stack>
        <Box
          onClick={() => {
            setSearchParams(
              {
                ...Object.fromEntries(searchParams.entries()),
                contentType: AssetCategory.UPLOADS,
              },
              {replace: true}
            );
          }}
        >
          <FileUploaderButton buttonLabel={translate('Upload')} />
        </Box>
      </Stack>
      <Stack
        direction="column"
        alignItems="center"
        flex={1}
        sx={{
          border: `1px solid ${designSystemToken('semantic.bg.tertiary')}`,
          borderRadius: '24px',
          backgroundColor: designSystemToken('semantic.bg.secondary'),
          width: '100%',
        }}
      >
        <Stack
          direction="row"
          justifyContent="space-between"
          width="100%"
          padding="16px"
          sx={{boxSizing: 'border-box'}}
        >
          <SwitchButton
            label={translate('Show X selected files only', {
              count: selectedAssets.length,
            })}
            checked={selectedOnlyMode}
            checkedTrackColor={designSystemToken('semantic.fg.primary')}
            sx={{
              fontSize: '16px',
            }}
            onChange={(event, checked) => {
              uiInteraction({
                item_name: 'show_selected_files_only',
                interaction_location: CONTENT_STUDIO_VIEW_NAMES.ASSETS,
                flow_name: FLOW_NAMES.CONTENT_STUDIO.ASSETS,
                interaction_details: checked ? 'on' : 'off',
              });
              setSelectedOnlyMode(checked);
              refetchResults();
            }}
            disabled={selectedAssets.length === 0}
          />
          <Stack
            direction="row"
            alignItems="center"
            gap="8px"
            justifySelf="flex-end"
          >
            {contentType === AssetCategory.RESULTS && (
              <FilterButton
                label={translate('Projects')}
                options={
                  filtersData?.projects.map(
                    (project: {id: string; name: string}) => ({
                      id: project.name,
                      label: project.name,
                    })
                  ) || []
                }
                onChange={(option) => {
                  contentStudioActions.handleFilter(
                    'project',
                    option.id,
                    'results'
                  );
                }}
                selectedOptionIds={resultsFilters.project}
                disabled={selectedOnlyMode}
              />
            )}
            <FilterButton
              label={translate('Format')}
              options={
                filtersData?.formats.map((aspectRatio: string) => ({
                  id: aspectRatio,
                  label: aspectRatio,
                })) || []
              }
              onChange={(option) => {
                uiInteraction({
                  item_name: 'projects',
                  interaction_location: CONTENT_STUDIO_VIEW_NAMES.ASSETS,
                  interaction_details: option.label,
                  flow_name: FLOW_NAMES.CONTENT_STUDIO.ASSETS,
                });
                contentStudioActions.handleFilter(
                  'format',
                  option.id,
                  contentType === AssetCategory.RESULTS ? 'results' : 'uploads'
                );
              }}
              selectedOptionIds={
                contentType === AssetCategory.RESULTS
                  ? resultsFilters.format
                  : uploadsFilters.format
              }
              disabled={selectedOnlyMode}
            />
            <FilterButton
              label={translate('Language')}
              options={
                filtersData?.languages.map((language: string) => ({
                  id: language,
                  label: language || '-',
                })) || []
              }
              onChange={(option) => {
                contentStudioActions.handleFilter(
                  'language',
                  option.id,
                  contentType === AssetCategory.RESULTS ? 'results' : 'uploads'
                );
              }}
              selectedOptionIds={
                contentType === AssetCategory.RESULTS
                  ? resultsFilters.language
                  : uploadsFilters.language
              }
              disabled={selectedOnlyMode}
            />
            {contentType === AssetCategory.RESULTS && (
              <FilterButton
                label={translate('Source')}
                options={
                  filtersData?.sources.map(
                    (source: {id: string; name: string}) => ({
                      id: source.id,
                      label: source.name,
                    })
                  ) || []
                }
                onChange={(option) => {
                  contentStudioActions.handleFilter(
                    'source',
                    option.id,
                    contentType === AssetCategory.RESULTS
                      ? 'results'
                      : 'uploads'
                  );
                }}
                selectedOptionIds={resultsFilters.source}
                disabled={selectedOnlyMode}
              />
            )}
          </Stack>
        </Stack>
        <FloatingPopperBar
          placement="bottom"
          widthCalculation={() => '70%'}
          placementMargin={34}
          isOpen={shouldOpenFloatingPopperBar()}
          className={styles.floatingPopperBarContainer}
          renderContent={() =>
            featureDetails ? (
              <SingleActionBar
                fileName={
                  selectedAssetsData?.find(
                    (asset) => asset.id === selectedAssets[0].id
                  )?.name
                }
                featureDetails={featureDetails}
                isDisabled={anyAssetNotReady}
                clearButtonProps={{
                  appearance: 'neutral',
                  mode: 'plain',
                  size: 'large',
                }}
                clearButtonText={translate('Clear selection')}
                clearButtonOnClick={() =>
                  contentStudioActions.setSelectedAssets([])
                }
              />
            ) : (
              <AssetActionsBar
                fileName={
                  selectedAssetsData?.find(
                    (asset) => asset.id === selectedAssets[0].id
                  )?.name
                }
                actionItems={actions}
                clearButtonProps={{
                  appearance: 'neutral',
                  mode: 'plain',
                  size: 'large',
                }}
                clearButtonText={translate('Clear selection')}
                clearButtonOnClick={() =>
                  contentStudioActions.setSelectedAssets([])
                }
              />
            )
          }
        >
          {contentType === AssetCategory.UPLOADS && !selectedOnlyMode ? (
            <AssetTable
              contentType={AssetCategory.UPLOADS}
              data={[...uploadingAssets, ...checkedUploadsData.assetsToDisplay]}
              fetchNextPage={fetchNextUploadsPage}
              isLoading={isUploadsLoading}
              isFetching={isUploadsFetching}
              selectedAssets={selectedAssets}
              isMultiSelect={featureDetails?.isMultipleSelect ?? true}
              setSelectedAssets={contentStudioActions.setSelectedAssets}
              classes={{
                container: styles.container,
                tableHeadRow: styles.tableHeadRow,
                tableBodyRow: styles.tableBodyRow,
                footer:
                  selectedAssets.length > 0 &&
                  [...uploadingAssets, ...checkedUploadsData.assetsToDisplay]
                    .length > 0
                    ? styles.highFooter || ''
                    : '',
              }}
              setQuerySorting={(sorting) =>
                handleSortingChange(
                  sorting,
                  contentStudioActions.setUploadsSorting
                )
              }
              sorting={
                uploadsSorting.field
                  ? [{id: uploadsSorting.field, desc: uploadsSorting.desc}]
                  : []
              }
              openPreview={openPreviewHandler}
              emptyStateComponent={emptyStateComponent()}
              selectedOnlyMode={selectedOnlyMode}
            />
          ) : null}
          {contentType === AssetCategory.RESULTS && !selectedOnlyMode ? (
            <AssetTable
              contentType={AssetCategory.RESULTS}
              data={resultsData?.pages.flatMap((page) => page) || []}
              fetchNextPage={fetchNextResultsPage}
              isLoading={isResultsLoading}
              isFetching={isResultsFetching}
              isResults
              selectedAssets={selectedAssets}
              isMultiSelect={featureDetails?.isMultipleSelect ?? true}
              setSelectedAssets={contentStudioActions.setSelectedAssets}
              classes={{
                container: styles.container,
                tableHeadRow: styles.tableHeadRow,
                tableBodyRow: styles.tableBodyRow,
                footer:
                  selectedAssets.length > 0 &&
                  (resultsData?.pages?.flatMap((page) => page).length ?? 0) > 0
                    ? styles.highFooter || ''
                    : '',
              }}
              setQuerySorting={(sorting) =>
                handleSortingChange(
                  sorting,
                  contentStudioActions.setResultsSorting
                )
              }
              sorting={
                resultsSorting.field
                  ? [{id: resultsSorting.field, desc: resultsSorting.desc}]
                  : []
              }
              openPreview={openPreviewHandler}
              emptyStateComponent={emptyStateComponent()}
              selectedOnlyMode={selectedOnlyMode}
            />
          ) : null}
          {selectedOnlyMode ? (
            <AssetTable
              data={selectedAssetsData || []}
              fetchNextPage={() => {}}
              isLoading={false}
              isFetching={false}
              isResults
              selectedAssets={selectedAssets}
              isMultiSelect={featureDetails?.isMultipleSelect ?? true}
              setSelectedAssets={contentStudioActions.setSelectedAssets}
              classes={{
                container: styles.container,
                tableHeadRow: styles.tableHeadRow,
                tableBodyRow: styles.tableBodyRow,
                footer:
                  selectedAssets.length > 0 ? styles.highFooter || '' : '',
              }}
              setQuerySorting={(sorting) =>
                handleSortingChange(
                  sorting,
                  contentStudioActions.setResultsSorting
                )
              }
              sorting={
                resultsSorting.field
                  ? [{id: resultsSorting.field, desc: resultsSorting.desc}]
                  : []
              }
              openPreview={openPreviewHandler}
              emptyStateComponent={emptyStateComponent()}
              selectedOnlyMode={selectedOnlyMode}
            />
          ) : null}
        </FloatingPopperBar>
      </Stack>
      <FolderPopper
        anchorElement={folderPopperElement}
        onClickAway={() => setFolderPopperElement(null)}
      />
      <RenamePopper
        anchorElement={renamePopperElement}
        onClickAway={() => setRenamePopperElement(null)}
      />
      {previewModalAsset() && (
        <AssetPreviewModal
          testID="ASSETS_PREVIEW_MODAL"
          open={openPreviewModal}
          onClose={() => {
            const interactionId = uuid();
            uiInteraction({
              item_name: 'preview_cancel_button',
              interaction_location: CONTENT_STUDIO_VIEW_NAMES.ASSET_PREVIEW,
              asset_id: previewModalAsset()?.id,
              interaction_id: interactionId,
              presentation_id: previewModalPresentationId,
            });
            viewDismissed({
              presentation_id: previewModalPresentationId,
              interaction_id: interactionId,
              reason: 'cancel_pressed',
            });
            setPreviewModalAssetIndex(0);
            setOpenPreviewModal(false);
          }}
          onNext={nextAssetClickHandler}
          onPrevious={previousAssetClickHandler}
          title={
            <AssetPreviewModalTitle
              currentIndex={previewModalAssetIndex}
              totalAssets={DisplayedAssets().length}
            />
          }
        >
          <AssetPreviewModalContent
            asset={previewModalAsset()!}
            presentationId={previewModalPresentationId}
          />
        </AssetPreviewModal>
      )}
    </Stack>
  );
}
